export interface RuntimeProfileConfig {
  serverUrl: string;
  clientUrl: string;
  todaUrl: string;
  webSocketEndPoint: string;
  googleMapsApiKey: string;
  homepageGoogleMapsApiKey: string;
  analyticsEnabled: boolean;
  googleTagManagerId: string;
  clarityProjectId: string;
  recaptchaSiteKey: string;
}

declare global {
  interface Window {
    __CITIZEX_CONFIG__?: Partial<RuntimeProfileConfig>;
  }
}

const normalize = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value.trim() : fallback;

const normalizeBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return fallback;
};

const hasConfiguredValue = (value: string): boolean =>
  value !== '' && !value.includes('your-');

const ensureTrailingSlash = (value: string): string =>
  value && !value.endsWith('/') ? `${value}/` : value;

const parseAbsoluteUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const isLoopbackHostname = (hostname: string): boolean =>
  ['localhost', '127.0.0.1', '::1'].includes(hostname.toLowerCase());

const getBrowserOrigin = (): string => {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return '';
  }

  return ensureTrailingSlash(window.location.origin);
};

const shouldUseBrowserOrigin = (
  configuredClientUrl: string,
  browserOrigin: string
): boolean => {
  if (!browserOrigin) {
    return false;
  }

  if (!hasConfiguredValue(configuredClientUrl)) {
    return true;
  }

  const configuredUrl = parseAbsoluteUrl(configuredClientUrl);
  const browserUrl = parseAbsoluteUrl(browserOrigin);
  if (!configuredUrl || !browserUrl) {
    return false;
  }

  if (configuredUrl.origin === browserUrl.origin) {
    return false;
  }

  return (
    isLoopbackHostname(configuredUrl.hostname) ||
    isLoopbackHostname(browserUrl.hostname)
  );
};

const resolveClientUrl = (value: unknown, fallback = ''): string => {
  const configuredClientUrl = normalize(value, fallback);
  const browserOrigin = getBrowserOrigin();

  if (shouldUseBrowserOrigin(configuredClientUrl, browserOrigin)) {
    return browserOrigin;
  }

  return ensureTrailingSlash(configuredClientUrl || browserOrigin);
};

export const getRuntimeConfig = (
  fallback: RuntimeProfileConfig
): RuntimeProfileConfig => {
  const runtimeConfig =
    typeof window !== 'undefined' ? window.__CITIZEX_CONFIG__ ?? {} : {};

  return {
    serverUrl: normalize(runtimeConfig.serverUrl, fallback.serverUrl),
    clientUrl: resolveClientUrl(runtimeConfig.clientUrl, fallback.clientUrl),
    todaUrl: normalize(runtimeConfig.todaUrl, fallback.todaUrl),
    webSocketEndPoint: normalize(
      runtimeConfig.webSocketEndPoint,
      fallback.webSocketEndPoint
    ),
    googleMapsApiKey: normalize(
      runtimeConfig.googleMapsApiKey,
      fallback.googleMapsApiKey
    ),
    homepageGoogleMapsApiKey: normalize(
      runtimeConfig.homepageGoogleMapsApiKey,
      fallback.homepageGoogleMapsApiKey
    ),
    analyticsEnabled: normalizeBoolean(
      runtimeConfig.analyticsEnabled,
      fallback.analyticsEnabled
    ),
    googleTagManagerId: normalize(
      runtimeConfig.googleTagManagerId,
      fallback.googleTagManagerId
    ),
    clarityProjectId: normalize(
      runtimeConfig.clarityProjectId,
      fallback.clarityProjectId
    ),
    recaptchaSiteKey: normalize(
      runtimeConfig.recaptchaSiteKey,
      fallback.recaptchaSiteKey
    ),
  };
};
